import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Link,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {format, parseISO} from 'date-fns';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';

import {LabelEmployee, LabelwinOrder, TimeEntryList} from './labelTaskTypes';
import {useLookup} from './labelwinMasterData';
import TimeEntryDialog from './TimeEntryDialog';

interface Props {
    /** Auftragsnummer, oder `null` = Dialog zu. */
    orderNumber: string | null;
    onClose: () => void;
    /** Nach Planung oder Abschluss — die aufrufende Liste lädt neu. */
    onChanged?: () => void;
}

/** Eine beschriftete Zeile; ohne Wert entfällt sie. */
const Row: React.FC<{label: string; children?: React.ReactNode}> = ({label, children}) => {
    if (children === null || children === undefined || children === '') return null;
    return (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 0, sm: 1.5}}>
            <Typography variant="body2" color="text.secondary" sx={{minWidth: 150, flexShrink: 0}}>
                {label}
            </Typography>
            <Typography variant="body2" component="div" sx={{minWidth: 0, wordBreak: 'break-word'}}>
                {children}
            </Typography>
        </Stack>
    );
};

/** Ein Freitext-Absatz; erhält die Zeilenumbrüche der Quelle. */
const Paragraph: React.FC<{label: string; text?: string | null}> = ({label, text}) => {
    if (!text || text.trim().length === 0) return null;
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{mb: 0.25}}>{label}</Typography>
            <Typography variant="body2" sx={{whiteSpace: 'pre-line'}}>{text.trim()}</Typography>
        </Box>
    );
};

const day = (iso?: string | null): string | undefined =>
    iso ? format(parseISO(iso), 'dd.MM.yyyy') : undefined;

const time = (iso?: string | null): string | undefined =>
    iso ? format(parseISO(iso), 'HH:mm') : undefined;

/**
 * Ein Kundendienst-Auftrag: ansehen, terminieren, Zeit buchen, abschließen.
 *
 * Der Auftrag ist der abrechenbare Vorgang — Zeit hängt an seiner Nummer, nie an einer Aufgabe.
 * Deshalb liegt hier alles zusammen, was man an einem laufenden Fall tut.
 *
 * **Terminieren schreibt in den Kalender des Monteurs**: ein Termintag mit Monteur zieht in
 * Labelwin den Plantafel-Balken nach. Der Abschnitt erscheint deshalb nur mit der Berechtigung
 * dafür und bei eingeschaltetem Schreibzugriff.
 */
export const OrderDetailDialog: React.FC<Props> = ({orderNumber, onClose, onChanged}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const canPlan = context.hasPermission('labelwin.orders.plan');
    const canComplete = context.hasPermission('labelwin.orders.complete');
    const canBookTime = context.hasPermission('labelwin.time.book');
    const canViewTimes = context.hasPermission('labelwin.time.viewall');

    const priorities = useLookup(context.api, 'Priorities');
    const orderStates = useLookup(context.api, 'OrderStates');

    const [order, setOrder] = useState<LabelwinOrder | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [times, setTimes] = useState<TimeEntryList | undefined>(undefined);
    const [employees, setEmployees] = useState<LabelEmployee[]>([]);
    const [bookOpen, setBookOpen] = useState(false);

    // Terminierung
    const [date, setDate] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [plannedHours, setPlannedHours] = useState('');
    const [technician, setTechnician] = useState<LabelEmployee | null>(null);
    const [priorityId, setPriorityId] = useState<number | ''>('');
    const [statusId, setStatusId] = useState<number | ''>('');
    const [dayAppointment, setDayAppointment] = useState(false);

    // Abschluss
    const [workDone, setWorkDone] = useState('');
    const [closeTask, setCloseTask] = useState(true);

    /** Übernimmt den geladenen Auftrag in die Eingabefelder. */
    const adopt = useCallback((loaded: LabelwinOrder, staff: LabelEmployee[]) => {
        setOrder(loaded);
        setDate(loaded.appointmentDate ?? '');
        setFrom(time(loaded.appointmentStart) ?? '');
        setTo(time(loaded.appointmentEnd) ?? '');
        setPlannedHours(loaded.plannedHours != null ? String(loaded.plannedHours) : '');
        setPriorityId(loaded.priorityId ?? '');
        setStatusId(loaded.statusId ?? '');
        setDayAppointment(loaded.dayAppointment);
        setTechnician(staff.find((e) => e.employeeId === loaded.technicianId) ?? null);
        setWorkDone('');
        setCloseTask(true);
    }, []);

    const load = useCallback(async () => {
        if (orderNumber === null) return;
        setLoading(true);
        setError(undefined);
        try {
            // Die Mitarbeiterliste wird für die Monteur-Auswahl gebraucht; sie kommt aus
            // demselben schlanken Endpunkt wie bei den Aufgaben.
            const [orderResponse, staffResponse] = await Promise.all([
                http.get<LabelwinOrder>(`/Orders/${encodeURIComponent(orderNumber)}`),
                http.get<LabelEmployee[]>('/Tasks/Employees').catch(() => ({data: []})),
            ]);
            setEmployees(staffResponse.data);
            adopt(orderResponse.data, staffResponse.data);
        } catch (e) {
            setError(apiErrorMessage(e, t('orders.loadFailed')));
            setOrder(undefined);
        } finally {
            setLoading(false);
        }
    }, [http, orderNumber, adopt, t]);

    useEffect(() => {
        void load();
    }, [load]);

    // Gebuchte Zeiten samt Summen — zeigt Buchungen aller Mitarbeiter und hängt deshalb an
    // einer eigenen Berechtigung.
    useEffect(() => {
        if (orderNumber === null || !canViewTimes) {
            setTimes(undefined);
            return;
        }

        let active = true;
        http.get<TimeEntryList>(`/TimeTracking/Order/${encodeURIComponent(orderNumber)}`)
            .then((r) => { if (active) setTimes(r.data); })
            .catch(() => { if (active) setTimes(undefined); });

        return () => { active = false; };
    }, [http, orderNumber, canViewTimes]);

    const plan = async (): Promise<void> => {
        if (order === null || orderNumber === null) return;
        setBusy(true);
        try {
            // Nur geänderte Felder — der Server ändert nur übergebene, alles Übrige bleibt.
            const patch: Record<string, unknown> = {};
            if (date && date !== (order?.appointmentDate ?? '')) patch.appointmentDate = date;
            if (from && from !== (time(order?.appointmentStart) ?? '')) patch.from = from;
            if (to && to !== (time(order?.appointmentEnd) ?? '')) patch.to = to;
            if (plannedHours && Number(plannedHours) !== order?.plannedHours) {
                patch.plannedHours = Number(plannedHours);
            }
            if (technician && technician.employeeId !== order?.technicianId) {
                patch.technicianId = technician.employeeId;
            }
            if (priorityId !== '' && priorityId !== order?.priorityId) patch.priorityId = priorityId;
            if (statusId !== '' && statusId !== order?.statusId) patch.statusId = statusId;
            if (dayAppointment !== order?.dayAppointment) patch.dayAppointment = dayAppointment;

            if (Object.keys(patch).length === 0) {
                enqueueSnackbar(t('orders.nothingChanged'), {variant: 'info'});
                return;
            }

            const r = await http.patch<LabelwinOrder>(
                `/Orders/${encodeURIComponent(orderNumber)}`, patch);
            adopt(r.data, employees);
            enqueueSnackbar(t('orders.planned'), {variant: 'success'});
            onChanged?.();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('orders.planFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const complete = async (): Promise<void> => {
        if (orderNumber === null) return;
        setBusy(true);
        try {
            const r = await http.post<LabelwinOrder>(
                `/Orders/${encodeURIComponent(orderNumber)}/complete`, undefined, {
                    workDone: workDone.trim() || null,
                    closeTask,
                });
            adopt(r.data, employees);
            enqueueSnackbar(t('orders.completed'), {variant: 'success'});
            onChanged?.();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('orders.completeFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const writable = order !== undefined && !order.done;

    return (
        <>
            <Dialog open={orderNumber !== null} onClose={busy ? undefined : onClose}
                    fullWidth maxWidth="sm">
                <DialogTitle sx={{pb: 1}}>
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                        <span>{t('orders.title', {order: orderNumber})}</span>
                        {order?.orderType && <Chip size="small" variant="outlined" label={order.orderType}/>}
                        {order?.priority && <Chip size="small" variant="outlined" label={order.priority}/>}
                        {order && (
                            <Chip size="small" color={order.done ? 'success' : 'info'} variant="outlined"
                                  label={order.status ?? (order.done ? t('orders.done') : t('orders.open'))}/>
                        )}
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    {loading && (
                        <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                            <CircularProgress size={24}/>
                        </Box>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}

                    {order && (
                        <Stack spacing={1.5}>
                            <Row label={t('orders.field.customer')}>{order.customerName}</Row>
                            <Row label={t('orders.field.contact')}>
                                <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                                    {order.contactName && <span>{order.contactName}</span>}
                                    {order.contactPhone && (
                                        <Link href={`tel:${order.contactPhone.replace(/[^\d+]/g, '')}`}>
                                            {order.contactPhone}
                                        </Link>
                                    )}
                                    {order.contactMail && (
                                        <Link href={`mailto:${order.contactMail}`}>{order.contactMail}</Link>
                                    )}
                                </Stack>
                            </Row>
                            <Row label={t('orders.field.technician')}>{order.technicianName}</Row>
                            <Row label={t('orders.field.appointment')}>
                                {order.appointmentDate
                                    ? [day(order.appointmentDate),
                                        time(order.appointmentStart) && time(order.appointmentEnd)
                                            ? `${time(order.appointmentStart)}–${time(order.appointmentEnd)}`
                                            : undefined,
                                        order.dayAppointment ? t('orders.dayAppointment') : undefined,
                                        order.fixedAppointment ? t('orders.fixedAppointment') : undefined,
                                    ].filter(Boolean).join(' · ')
                                    : t('orders.noAppointment')}
                            </Row>
                            <Row label={t('orders.field.plannedHours')}>{order.plannedHours}</Row>
                            <Row label={t('orders.field.project')}>{order.projectNumber}</Row>
                            <Row label={t('orders.field.supportId')}>{order.supportId}</Row>
                            <Row label={t('orders.field.completedAt')}>{day(order.completedAt)}</Row>
                            {times && (
                                <Row label={t('orders.field.bookedTime')}>
                                    {t('timeTracking.totals', {
                                        total: times.totalHours,
                                        open: times.openHours,
                                        entries: times.count,
                                    })}
                                </Row>
                            )}

                            <Paragraph label={t('orders.field.subject')} text={order.subject}/>
                            <Paragraph label={t('orders.field.problem')} text={order.comment}/>
                            <Paragraph label={t('orders.field.officeNote')} text={order.officeNote}/>
                            <Paragraph label={t('orders.field.technicianNote')} text={order.technicianNote}/>
                            <Paragraph label={t('orders.field.workDone')} text={order.workDone}/>

                            {canPlan && writable && (
                                <>
                                    <Divider sx={{mt: 1}}/>
                                    <Typography variant="subtitle2">{t('orders.planSection')}</Typography>
                                    <Alert severity="info">{t('orders.planHint')}</Alert>

                                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                        <TextField fullWidth size="small" type="date"
                                                   label={t('orders.field.appointmentDate')}
                                                   value={date}
                                                   slotProps={{inputLabel: {shrink: true}}}
                                                   onChange={(e) => setDate(e.target.value)}/>
                                        <TextField fullWidth size="small" type="number"
                                                   label={t('orders.field.plannedHours')}
                                                   value={plannedHours}
                                                   slotProps={{htmlInput: {min: 0, step: 0.25}}}
                                                   onChange={(e) => setPlannedHours(e.target.value)}/>
                                    </Stack>

                                    <Stack direction="row" spacing={2}>
                                        <TextField fullWidth size="small" type="time"
                                                   label={t('orders.field.from')}
                                                   value={from}
                                                   slotProps={{inputLabel: {shrink: true}}}
                                                   onChange={(e) => setFrom(e.target.value)}/>
                                        <TextField fullWidth size="small" type="time"
                                                   label={t('orders.field.to')}
                                                   value={to}
                                                   slotProps={{inputLabel: {shrink: true}}}
                                                   onChange={(e) => setTo(e.target.value)}/>
                                    </Stack>

                                    <Autocomplete
                                        size="small"
                                        options={employees}
                                        value={technician}
                                        onChange={(_, value) => setTechnician(value)}
                                        getOptionLabel={(option) =>
                                            option.name ?? option.labelUser ?? String(option.employeeId)}
                                        isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t('orders.field.technician')}/>
                                        )}
                                    />

                                    {priorities.length > 0 && (
                                        <TextField select fullWidth size="small"
                                                   label={t('orders.field.priority')}
                                                   value={priorityId}
                                                   onChange={(e) => setPriorityId(
                                                       e.target.value === '' ? '' : Number(e.target.value))}>
                                            <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                                            {priorities.map((value) => (
                                                <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}

                                    {orderStates.length > 0 && (
                                        <TextField select fullWidth size="small"
                                                   label={t('orders.field.status')}
                                                   value={statusId}
                                                   onChange={(e) => setStatusId(
                                                       e.target.value === '' ? '' : Number(e.target.value))}>
                                            <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                                            {orderStates.map((value) => (
                                                <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}

                                    <FormControlLabel
                                        control={<Checkbox checked={dayAppointment}
                                                           onChange={(e) => setDayAppointment(e.target.checked)}/>}
                                        label={t('orders.field.dayAppointment')}/>

                                    <Box>
                                        <Button variant="contained" size="small" disabled={busy}
                                                onClick={() => void plan()}>
                                            {t('orders.plan')}
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {canComplete && writable && (
                                <>
                                    <Divider sx={{mt: 1}}/>
                                    <Typography variant="subtitle2">{t('orders.completeSection')}</Typography>
                                    <TextField fullWidth size="small" multiline minRows={2}
                                               label={t('orders.field.workDone')}
                                               value={workDone}
                                               onChange={(e) => setWorkDone(e.target.value)}
                                               helperText={t('orders.workDoneHint')}/>
                                    <FormControlLabel
                                        control={<Checkbox checked={closeTask}
                                                           onChange={(e) => setCloseTask(e.target.checked)}/>}
                                        label={t('orders.closeTask')}/>
                                    <Box>
                                        <Button variant="outlined" color="success" size="small" disabled={busy}
                                                onClick={() => void complete()}>
                                            {t('orders.complete')}
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {order.done && <Alert severity="success">{t('orders.alreadyDone')}</Alert>}
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    {canBookTime && orderNumber !== null && (
                        <Button onClick={() => setBookOpen(true)} disabled={busy}>
                            {t('timeTracking.book')}
                        </Button>
                    )}
                    <Button variant="contained" onClick={onClose} disabled={busy}>
                        {t('orders.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <TimeEntryDialog
                open={bookOpen}
                orderNumber={orderNumber}
                onClose={() => setBookOpen(false)}
                onBooked={() => { void load(); onChanged?.(); }}/>
        </>
    );
};

export default OrderDetailDialog;
